import { ServiceAboutSection } from "../components/ServiceAboutSection";
import { useServiceDetails } from "./useServiceDetails";
import "./ServiceDetail.css";
import { DetailBreadcrumb } from "../components/DetailBreadcrumb";
import { ClosedPostNotice } from "../components/ClosedPostNotice";
import { useEffect, useState } from "react";
import { useRoute, Link, useLocation } from "wouter";
import { Bookmark, Info, MapPin, MessageSquare, ReceiptText, ShieldCheck, Star } from "lucide-react";
import { ImageCarousel } from "../components/ImageCarousel";
import { ListingProfileCard } from "../components/ListingProfileCard";
import { ContactModal } from "../components/ContactModal";
import { Feature } from "../components/ui/kit";
import { DetailLoading, DetailNotFound } from "../components/DetailStates";
import { RatingSummary, ReviewList, Stars } from "../components/Reviews";
import { useListing } from "../lib/useListing";
import { PreviousWork } from "../components/PreviousWork";
import { services as mockServices, peso } from "../data/mock";
import { setMeta, resetMeta } from "../lib/seo";
import { SaveButton } from "../components/SaveButton";
import { MobileActionBar } from "../components/MobileActionBar";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { messagesHref } from "../lib/actions";
import { useInPageActionVisibility } from "../lib/useInPageActionVisibility";
import { useI18n } from "../lib/i18n";

export default function ServiceDetail() {
  const { t } = useI18n();
  const [, params] = useRoute("/s/:id");
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const id = params?.id || "";
  const { item: raw, state } = useListing("services", id, mockServices);
  const businessIdentity = raw?.postingIdentity?.kind === "business" ? raw.postingIdentity : null;
  const isBusiness = !!businessIdentity;
  const ownerUserId = Number(raw?.ownerUserId);
  const categoryQuery = typeof raw?.category === "string"
    ? raw.category
    : Array.isArray(raw?.tags) && typeof raw.tags[0] === "string" ? raw.tags[0] : "";

  const [quoteOpen, setQuoteOpen] = useState(false);
  const [reviewsOpen, setReviewsOpen] = useState(false);
  const { actionRef, isVisible } = useInPageActionVisibility<HTMLDivElement>(`${id}:${state}`, true);

  const { providerReviews, relatedServices, providerVerification, reviewState } = useServiceDetails(ownerUserId, isBusiness, categoryQuery, id);

  useEffect(() => {
    if (raw) setMeta({
      title: `${raw.title} — KontrataPH`,
      description: [raw.by ? `by ${raw.postingIdentity?.kind === "business" ? raw.postingIdentity.name : raw.by}` : "", raw.city || "", raw.fromPrice ? `from ₱${Number(raw.fromPrice).toLocaleString("en-PH")}` : ""].filter(Boolean).join(" · ") || undefined,
    });
    return () => resetMeta();
  }, [raw]);

  if (state === "loading") return <DetailLoading label="Loading service…" />;
  if (state === "notfound" || !raw) return <DetailNotFound title="Service not found" backHref="/marketplace?tab=Services" backLabel="Browse Services" />;

  const service = { ...raw, from: raw.fromPrice ?? raw.from ?? 0 };
  const tags: string[] = Array.isArray(service.tags)
    ? service.tags.filter((tag: unknown): tag is string => typeof tag === "string")
    : [];
  const included = typeof service.whatsIncluded === "string"
    ? service.whatsIncluded.split("\n").map((item: string) => item.trim()).filter(Boolean)
    : tags;
  const requirements = typeof service.requirements === "string" ? service.requirements.trim() : "";
  const previousWork = typeof service.previousWork === "string" ? service.previousWork : "";
  const description = typeof service.description === "string" && service.description.trim()
    ? service.description.trim()
    : `${service.title} offered by ${businessIdentity?.name || service.by}, serving ${service.city}. Request a quotation for a scope and price tailored to your needs.`;

  const isSelf = !!user && service.ownerUserId === user.id;
  const msgHref = messagesHref(service.ownerUserId, { type: "service", id, name: service.title });
  const ctaDisabled = !!raw.hidden || !!raw.ownerClosed || raw.status === "closed" || raw.status === "archived" || (!!user && (isSelf || !msgHref));
  const disabledReason = raw.hidden || raw.ownerClosed || raw.status === "closed" || raw.status === "archived" ? t("post.owner.closedNotice") : isSelf ? "This is your listing" : "Not available on this sample listing";
  const ratingAverage = providerReviews?.average ?? 0;
  const ratingCount = providerReviews?.count ?? 0;
  const writtenReviews = providerReviews?.reviews.filter((review) => review.comment?.trim()) ?? [];
  // [verification.public-signals](../../../docs/product/profiles-and-verification.md#verification-public-signals): company names never inherit their owner's personal trust.
  const providerName = businessIdentity ? businessIdentity.name : !service.company && providerVerification?.name ? providerVerification.name : service.by;

  const openQuote = () => {
    if (!user) return navigate("/login");
    if (ctaDisabled) return;
    setQuoteOpen(true);
  };

  return (
    <div className={`service-detail-a container-x px-0 sm:px-0 lg:px-6 py-0 lg:py-6 ${!isVisible ? "pb-24 lg:pb-6" : ""}`}>
      <ClosedPostNotice post={raw || {}} />
      <DetailBreadcrumb title={service.title} fallback={{ href: "/marketplace?tab=Services", label: "Services" }} className="px-4 py-4 text-sm lg:mb-4 lg:px-0 lg:py-0" />

      <div className="service-detail-layout flex flex-col lg:grid lg:grid-cols-[minmax(0,1fr)_360px] lg:gap-4 lg:items-start">
        <div className="service-detail-main contents lg:block lg:min-w-0">
          <div className="relative min-w-0 order-1 lg:order-none">
            <ImageCarousel type="service" id={id} compact narrowThumbnails mobileService beuiViewer />
            <div className="absolute right-4 top-4 z-30 lg:hidden">
              {user
                ? <SaveButton kind="service" refId={id} imageOverlay className="h-10 w-10" iconClassName="h-6 w-6" />
                : <button type="button" onClick={() => navigate("/login")} aria-label="Save service" className="grid h-10 w-10 place-items-center text-white hover:text-white/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"><Bookmark className="h-6 w-6" /></button>}
            </div>
          </div>

          <section className="card order-2 p-5 rounded-none border-x-0 border-t-0 shadow-none lg:order-none lg:rounded-xl2 lg:border lg:shadow-card">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h1 className="text-2xl font-extrabold text-navy leading-tight">{service.title}</h1>
              </div>
              <div className="hidden lg:block">{user
                ? <SaveButton kind="service" refId={id} label="Save" className="shrink-0 px-3" />
                : <button type="button" onClick={() => navigate("/login")} className="btn-outline shrink-0 px-3">♡ Save</button>}</div>
            </div>
          </section>

          <ServiceAboutSection key={id} description={description} tags={tags} />

          <section className="card order-4 rounded-none border-x-0 border-t-8 border-t-paper shadow-none p-5 lg:order-none lg:rounded-xl2 lg:border lg:border-t-line lg:shadow-card">
            <h2 className="font-bold text-navy text-lg mb-3">{t("detail.service.included")}</h2>
            {included.length > 0
              ? <ul className="grid sm:grid-cols-2 xl:grid-cols-3 gap-2.5">{included.map((item: string, index: number) => <Feature key={`${item}-${index}`}>{item}</Feature>)}</ul>
              : <p className="text-sm text-mut">{t("detail.service.scopeOnRequest", "Scope discussed on request.")}</p>}
          </section>

          {requirements && (
            <section className="card order-5 rounded-none border-x-0 border-t-8 border-t-paper shadow-none p-5 lg:order-none lg:rounded-xl2 lg:border lg:border-t-line lg:shadow-card">
              <h2 className="font-bold text-navy text-lg mb-2">{t("detail.service.clientProvide", "What the client needs to provide")}</h2>
              <p className="text-sm text-mut whitespace-pre-line">{requirements}</p>
            </section>
          )}

          <div className="order-6 lg:order-none"><PreviousWork text={previousWork} type="service" id={id} showcase /></div>

          {!isBusiness && <section className="card order-8 rounded-none border-x-0 border-t-8 border-t-paper shadow-none p-5 lg:order-none lg:rounded-xl2 lg:border lg:border-t-line lg:shadow-card">
            <div className="flex items-center justify-between gap-3 mb-4">
              <h2 className="font-bold text-navy text-lg">{t("detail.service.reviews")}</h2>
            </div>
            <div className="lg:hidden">
              {ratingCount > 0 ? (
                <div className="flex items-center gap-4">
                  <strong className="text-4xl font-extrabold text-navy">{ratingAverage.toFixed(1)}</strong>
                  <div><Stars value={ratingAverage} size={18} /><p className="text-xs text-mut mt-1">Based on {ratingCount} provider review{ratingCount === 1 ? "" : "s"}</p></div>
                </div>
              ) : <p className="text-sm text-mut">{t("detail.service.noReviews")}</p>}
              {writtenReviews.length > 0 ? (
                <div className="mt-4 border-t border-line">
                  {writtenReviews.slice(0, reviewsOpen ? undefined : 2).map((review) => (
                    <article key={review.id} className="py-3 border-b border-line">
                      <div className="flex items-center justify-between gap-2">
                        <strong className="text-sm text-navy truncate">{review.raterName || "Member"}</strong>
                        <Stars value={review.rating} size={14} />
                      </div>
                      <p className="text-sm text-mut mt-1">{review.comment}</p>
                    </article>
                  ))}
                  {writtenReviews.length > 2 && (
                    <button type="button" onClick={() => setReviewsOpen((open) => !open)} aria-expanded={reviewsOpen}
                      className="block ml-auto mt-3 text-sm font-semibold text-blue-700 hover:text-blue-900">
                      {reviewsOpen ? t("common.showLess", "Show less") : t("common.viewMore", "View more")}
                    </button>
                  )}
                </div>
              ) : reviewState === "loading" ? <p className="text-sm text-mut mt-4">Loading provider feedback…</p>
                : reviewState === "unavailable" ? <p className="text-sm text-mut mt-4">Provider feedback is temporarily unavailable.</p>
                  : ratingCount > 0 ? <p className="text-sm text-mut mt-4">{t("detail.service.noFeedback")}</p> : null}
            </div>
            <div className="hidden lg:grid md:grid-cols-[minmax(240px,0.85fr)_minmax(0,1.15fr)] gap-5 items-center">
              <RatingSummary average={ratingAverage} count={ratingCount} breakdown={providerReviews?.breakdown} />
              <div className="min-w-0">
                {providerReviews?.reviews.length
                  ? <ReviewList reviews={providerReviews.reviews.slice(0, 5)} />
                  : reviewState === "loading"
                    ? <p className="text-sm text-mut">Loading provider feedback…</p>
                    : reviewState === "unavailable"
                      ? <p className="text-sm text-mut">Provider feedback is temporarily unavailable.</p>
                      : <p className="text-sm text-mut">{t("detail.service.noFeedback")}</p>}
              </div>
            </div>
            {providerReviews && providerReviews.reviews.length > 5 && <button type="button" onClick={() => setReviewsOpen((open) => !open)} aria-expanded={reviewsOpen} className="hidden lg:block mt-4 text-sm font-semibold text-blue-700 hover:text-blue-900">{reviewsOpen ? t("common.showLess", "Show less") : t("common.viewMore", "View more")}</button>}
            {reviewsOpen && providerReviews && <div className="hidden lg:block mt-4"><ReviewList reviews={providerReviews.reviews.slice(5)} /></div>}
          </section>}
        </div>

        <aside className="service-detail-aside contents lg:block lg:sticky lg:top-24 lg:self-start lg:h-fit">
          <div className="order-2 lg:order-none">
            <ListingProfileCard postingIdentity={raw.postingIdentity} title={t("detail.service.aboutProvider")}
              profile={{ name: providerName || t("detail.item.member"), role: service.company ? t("detail.profile.company") : t("detail.profile.individual"),
                location: service.city, memberSince: service.company ? null : providerVerification?.memberSince,
                imageUrl: service.company ? null : api.apiAssetUrl(providerVerification?.imageUrl),
                href: !service.company && Number.isSafeInteger(ownerUserId) && ownerUserId > 0 ? `/u/${ownerUserId}` : null,
                companyVerified: service.company && providerVerification?.company,
                identityVerified: !service.company && providerVerification?.identity,
                verifiedProfessions: service.company ? [] : providerVerification?.professions }}
              statistics={[{ label: t("detail.service.servicesListed"), value: service.company ? null : providerVerification?.services }]}
              rating={providerReviews ? { average: providerReviews.average, count: providerReviews.count } : null} />
          </div>
          <section className="card order-2 rounded-none border-x-0 border-t-0 shadow-none p-5 lg:order-none lg:rounded-xl2 lg:border lg:shadow-card">
            <div className="text-xs text-mut">{service.from > 0 ? t("detail.service.startingAt") : t("detail.service.price")}</div>
            <div className="text-xl font-extrabold text-navy mt-1 inline-flex items-center gap-1.5">
              {service.from > 0 ? peso(service.from) : t("detail.service.requestQuote")}
              <Info className="hidden lg:block w-4 h-4" aria-label="Pricing information" />
            </div>
            <p className="text-xs text-mut mt-1">{t("detail.service.finalPrice")}</p>

            <div ref={actionRef} className="mt-4 flex items-stretch gap-2">
              <button type="button" onClick={openQuote} disabled={ctaDisabled} title={ctaDisabled ? disabledReason : undefined}
                className="btn-red min-w-0 flex-1 disabled:opacity-50 disabled:cursor-not-allowed">
                <ReceiptText className="hidden lg:block w-4 h-4" />{t("detail.service.requestQuote")}
              </button>
              <button type="button" onClick={() => user ? (msgHref && navigate(msgHref)) : navigate("/login")} disabled={ctaDisabled} title={ctaDisabled ? disabledReason : undefined} aria-label={t("detail.service.messageProvider")}
                className="btn-outline w-11 shrink-0 p-0 disabled:opacity-50 disabled:cursor-not-allowed">
                <MessageSquare className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
            <p className="text-[11px] text-mut flex items-center gap-1 mt-3 lg:justify-center"><ShieldCheck className="w-3 h-3" />{t("detail.service.escrowHire")}</p>

          </section>

          <section className="hidden lg:block card p-4">
            <h2 className="font-bold text-navy flex items-center gap-2"><MapPin className="w-4 h-4" />{t("detail.service.area")}</h2>
            <p className="text-sm text-mut mt-2">{t("detail.service.serves", "Serves")} {service.city || t("detail.service.locationOnRequest", "location available on request")}</p>
          </section>

          <section className="card order-7 rounded-none border-x-0 border-t-8 border-t-paper shadow-none p-5 lg:order-none lg:rounded-xl2 lg:border lg:border-t-line lg:shadow-card lg:p-4">
            <div className="flex items-center justify-between gap-2 mb-3">
              <h2 className="font-bold text-navy">Similar Services</h2>
              <Link href="/marketplace?tab=Services" className="text-sm font-semibold text-blue-700 hover:text-blue-900">View all →</Link>
            </div>
            {relatedServices.length > 0 ? (
              <div className="grid lg:grid-cols-1 lg:gap-2">
                {relatedServices.map((related) => {
                  const price = Number(related.fromPrice ?? related.from) || 0;
                  return (
                    <Link key={related.id} href={`/s/${encodeURIComponent(related.id)}`} className="border-t border-line py-3 lg:border lg:rounded-lg lg:p-3 hover:border-blue-300 transition-colors">
                      <div className="font-semibold text-sm text-navy line-clamp-2">{related.title}</div>
                      <div className="flex items-center justify-between gap-2 mt-2 text-xs">
                        <span className="inline-flex items-center gap-1 text-mut"><Star className="w-3 h-3 fill-brand-gold text-brand-gold" />{Number(related.rating || 0).toFixed(1)} ({Number(related.reviews || 0)})</span>
                        <span className="font-bold text-navy">{price > 0 ? `From ${peso(price)}` : "Request a quote"}</span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            ) : <p className="text-sm text-mut">{t("detail.service.noSimilar")}</p>}
          </section>
        </aside>
      </div>

      {!isVisible && <MobileActionBar>
        <div className="shrink-0">
          <div className="text-[11px] text-mut leading-none">{service.from > 0 ? "Starting at" : "Price"}</div>
          <div className="text-lg font-extrabold text-navy leading-tight">{service.from > 0 ? peso(service.from) : t("detail.service.quote", "Quote")}</div>
        </div>
        <button type="button" onClick={openQuote} disabled={ctaDisabled} title={ctaDisabled ? disabledReason : undefined}
          className="btn-red flex-1 disabled:opacity-50 disabled:cursor-not-allowed">{t("detail.service.requestQuote")}</button>
      </MobileActionBar>}

      {/* Keep the service reference on the request while reusing the personal conversation. */}
      {quoteOpen && service.ownerUserId && (
        <ContactModal
          open onClose={() => setQuoteOpen(false)}
          toUserId={service.ownerUserId} toName={service.by} contextType="service" contextId={id}
          subject={`Quote request — ${service.title}`}
          requestQuote
        />
      )}
    </div>
  );
}
